import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';

import {
  makeRowColumn,
  getHeadObj,
  converParameters,
  dataConverter,
  dayColor,
  setNumberFormat,
  getHost,
} from 'lib/commonLib';
import {
  budgetCategoryDetail,
  budgetTargetingDetail,
  budgetDayGraph,
} from 'lib/api/budget';
import { handleSortChange } from 'modules/common';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import SortLabelWrap from 'components/molecules/MbTable/SortLabelWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import Highchart from 'components/organisms/Chart/Highchart';

import BudgetCommonFilter from './BudgetCommonFilter';

const DetailModal = (props) => {
  const {
    location,
    handlePortalUrl,
    productList,
    categoryList,
    handleClickSnackbar,
  } = props;
  const { t } = useTranslation();
  const isTargetingPage = !!queryString.parse(location.search).type;
  let initParams = {};
  let convertKeys = [
    'ctgrSeqs',
    'stdCode',
    'uri',
    'type',
    'statsDttm',
    'isPop',
  ];
  const theme = useTheme();

  const newInitParams = dataConverter(
    queryString.parse(location.search),
    convertKeys
  );
  initParams = { ...initParams, ...newInitParams };

  if (isTargetingPage) {
    initParams = { ...initParams, sort: 'asc', order: 'ADVRTS_TP_CODE_STR' };
  } else {
    initParams = {
      ...initParams,
      sort: 'desc',
      order: 'STATS_DTTM',
      sDate: moment(newInitParams.statsDttm).add(-7, 'day').format('YYYYMMDD'),
      eDate: moment(newInitParams.statsDttm).format('YYYYMMDD'),
    };
    delete initParams['statsDttm'];
  }

  let initCustomized = {
    FIXED: { merged: true },
    DD_EXHS_AMT: { orderFlag: true },
    ADVRTS_TP_CODE_STR: { orderFlag: true },
    CTGR_NM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CTGR_NM');
        let result;
        result = makeRowColumn(
          rowObj['CTGR_NM'],
          bodyItem1.type,
          bodyItem1.format
        );
        return result;
      },
    },
    CTGR_SEQ: { merged: true },
    ADVRTS_TP_CODE: { merged: true },
  };

  const initOptions = {
    chart: { type: 'line', backgroundColor: theme.palette.common.bgColor },
    colors: ['#000'],
    title: {
      text: t('일자별 예산 비중도'),
      style: { fontWeight: 'bold' },
    },
    credits: { enabled: false },
    legend: { enabled: true },
    xAxis: {
      type: 'category',
      plotLines: [
        { label: { align: 'center', style: { color: 'gray' } }, zIndex: 10000 },
      ],
    },
    series: [],
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [customized, setCustomized] = useState(initCustomized);
  const [optionList, setOptionList] = useState(initOptions);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!isTargetingPage) {
      budgetDayGraph({
        params: {
          sDate: params.sDate,
          eDate: params.eDate,
          ctgrSeqs: params.ctgrSeqs,
          stdCode: params.stdCode,
          order: 'statsDttm',
          uri: '/report/budget/category',
        },
      })
        .then((response) => {
          const { data } = response.data;
          let newOptions = { ...initOptions };
          const newDataKey = Object.keys(data);
          let newData = [];
          const length = data[newDataKey[0]]['data'].length;
          for (let i = 0; i < length; i++) {
            newData.push({
              y: data['DD_EXHS_AMT']['data'][i]['val'],
              name: data['STATS_DTTM']['data'][i]['val'],
              WEB_EXHS_RATE: data['WEB_EXHS_RATE']['data'][i]['val'],
              MOBILE_EXHS_RATE: data['MOBILE_EXHS_RATE']['data'][i]['val'],
            });
          }
          newOptions = {
            ...newOptions,
            series: [{ 'name': t('일자별 예산 금액'), 'data': newData }],
            xAxis: {
              ...newOptions['xAxis'],
              tickAmount: 2,
              labels: {
                formatter: function () {
                  return moment(String(this.value)).format('MM-DD');
                },
              },
            },
            yAxis: {
              title: {
                text: '(' + t('백만') + ')',
                align: 'high',
                textAlign: 'right',
                rotation: 0,
                offset: 0,
                margin: 0,
                y: -10,
                x: -10,
              },
              labels: {
                format: '{value:.,0f}',
                formatter: function () {
                  return setNumberFormat(this.value / 1000000);
                },
              },
            },
            tooltip: {
              formatter: function () {
                let sHtml =
                  '<b>' +
                  moment(String(this.points[0].key)).format('YYYY-MM-DD') +
                  '</b>';
                sHtml +=
                  '<br><b>- ' +
                  t('총 예산') +
                  ': </b>' +
                  setNumberFormat(this['y']) +
                  '<br><b>- ' +
                  'PC' +
                  ': </b>' +
                  setNumberFormat(
                    this.points[0].point['WEB_EXHS_RATE'],
                    'percent',
                    '0,0.0'
                  ) +
                  '<br><b>- ' +
                  'Mobile' +
                  ': </b>' +
                  setNumberFormat(
                    this.points[0].point['MOBILE_EXHS_RATE'],
                    'percent',
                    '0,0.0'
                  );
                return sHtml;
              },
              shared: true,
            },
          };
          setOptionList(newOptions);
          setStatus('');
        })
        .catch((error) => {
          handleClickSnackbar({ message: error, option: { type: 'error' } });
          setStatus('error');
        });
    }
    handleCustom(params);

    // eslint-disable-next-line
  }, [params]);

  const handleCustom = (propsParams) => {
    let newCustomized = {
      ...initCustomized,
      STATS_DTTM: {
        makeBody: (rowObj, headsMap) => {
          let result;
          const showWindowUrl = converParameters({
            url: getHost('new') + '/report/budget/share/detail',
            statsDttm: rowObj['STATS_DTTM'],
            stdCode: propsParams.stdCode,
            uri: '/report/budget/targetingDetail',
            type: 'targeting',
            isPop: 1,
          });
          result = (
            <IconAreaWrap
              text={dayColor(
                makeRowColumn(rowObj['STATS_DTTM'], 'date', 'YYYY-MM-DD')
              )}
              icon="faExternalLink"
              toolTipTitle={t('해당 일자의 타게팅별 상세 상수 팝업')}
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
          );
          return result;
        },
        merged: isTargetingPage,
      },
    };
    for (let key of productList) {
      const styleObj =
        key === 'BANNER'
          ? {
              borderLeft: '1px solid #e5e8eb',
              borderRight: '1px solid #e5e8eb',
            }
          : { borderRight: '1px solid #e5e8eb' };
      newCustomized[key + '_WEB_CNST'] = {
        makeHead: (headsMap) => {
          let title = '';
          if (key === 'BANNER') {
            title = t('배너');
          } else if (key === 'ICOVER') {
            title = t('아이커버');
          } else if (key === 'NATIVE') {
            title = t('네이티브');
          }

          return (
            <SpanningTableContents topTitle={title} type="head">
              <div style={{ width: 50 }}>
                <SortLabelWrap
                  params={propsParams}
                  label="PC"
                  orderKey={key + '_WEB_CNST'}
                  onClick={(e) =>
                    getParam(handleSortChange(propsParams, key + '_WEB_CNST'))
                  }
                />
              </div>
              <div style={{ width: 50 }}>
                <SortLabelWrap
                  params={propsParams}
                  label="Mobile"
                  orderKey={key + '_MOBILE_CNST'}
                  onClick={(e) =>
                    getParam(
                      handleSortChange(propsParams, key + '_MOBILE_CNST')
                    )
                  }
                />
              </div>
            </SpanningTableContents>
          );
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, key + '_WEB_CNST');
          const bodyItem2 = getHeadObj(headsMap, key + '_MOBILE_CNST');
          const naText = (
            <span style={{ color: theme.palette.common.gray2 }}>(N/A)</span>
          );
          const isTotalRow = rowObj['FIXED'] === 0;

          return (
            <SpanningTableContents>
              <div style={{ width: 50 }}>
                {rowObj[key + '_WEB_CNST'] !== null
                  ? makeRowColumn(
                      rowObj[key + '_WEB_CNST'],
                      bodyItem1.type,
                      isTotalRow ? '0.00%' : bodyItem1.format
                    )
                  : naText}
              </div>
              <div style={{ width: 50 }}>
                {rowObj[key + '_MOBILE_CNST'] !== null
                  ? makeRowColumn(
                      rowObj[key + '_MOBILE_CNST'],
                      bodyItem2.type,
                      isTotalRow ? '0.00%' : bodyItem2.format
                    )
                  : naText}
              </div>
            </SpanningTableContents>
          );
        },
        orderFlag: false,
        headStyle: styleObj,
        bodyStyle: styleObj,
      };
      newCustomized[key + '_MOBILE_CNST'] = { merged: true };
    }
    setCustomized(newCustomized);
  };

  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      handleCustom(initParams);
    }
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
      handleCustom(newParams);
    }
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <BudgetCommonFilter
          params={params}
          getParam={getParam}
          handleOnReset={handleOnReset}
          categoryList={categoryList}
          initialization={initialization}
        />
      </NewFilterWrap>

      {params['type'] !== 'targeting' && (
        <Highchart options={optionList} loading={status} />
      )}
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={
          !!params['type'] ? budgetTargetingDetail : budgetCategoryDetail
        }
        customized={customized}
        useFetch={false}
        useTotal={false}
        useSearch={false}
        useStoreLoading={false}
        usePagenation={false}
        useFullScreen={false}
        rowsPerPage={-1}
        title={
          !!params['type'] &&
          '기준일 : ' + moment(params.statsDttm).format('YYYY.MM.DD')
        }
      />
    </>
  );
};

export default withRouter(DetailModal);
